// src/features/portfolio-optimization/components/RiskReturnScatter.tsx
//
// Zone 2 — the Institutional "Tilt" (Wealth Manager View), part 2.
//
// A risk/return bubble chart: x = annualised volatility (risk), y = the
// Black-Litterman view return Q (the ML's expected edge), bubble area = the
// active target weight, colour = the ML side (green long / red short). The
// "efficient" names sit top-left (high edge, low risk); the dots the eye is
// drawn to bottom-right are the ones taking risk without a commensurate view.
//
// Built on recharts <ScatterChart> with a custom shape so we can size each
// bubble by conviction-weighted capital and keep the dark-mode palette.

import {
    CartesianGrid,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from 'recharts';

export interface RiskReturnPoint {
    symbol: string;
    /** Annualised volatility in percent. */
    volatility: number;
    /** Black-Litterman view return Q in percent. */
    expectedReturn: number;
    /** Target weight in percent (drives bubble size). */
    weight: number;
    /** Calibrated master probability (0..1). */
    probability: number;
    /** +1 long / -1 short. */
    side: number;
}

interface RiskReturnScatterProps {
    points: RiskReturnPoint[];
}

const ScatterTooltip = ({
    active,
    payload,
}: {
    active?: boolean;
    payload?: { payload: RiskReturnPoint }[];
}) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg space-y-0.5">
            <p className="font-bold text-slate-900 dark:text-white">
                {p.symbol}{' '}
                <span
                    className={
                        p.side >= 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-rose-600 dark:text-rose-400'
                    }
                >
                    {p.side >= 0 ? 'LONG' : 'SHORT'}
                </span>
            </p>
            <p className="font-mono text-slate-500 dark:text-slate-400">
                Risk (vol): {p.volatility.toFixed(1)}%
            </p>
            <p className="font-mono text-slate-500 dark:text-slate-400">
                View return Q: {p.expectedReturn.toFixed(2)}%
            </p>
            <p className="font-mono text-slate-500 dark:text-slate-400">
                Weight: {p.weight.toFixed(2)}% · p={p.probability.toFixed(2)}
            </p>
        </div>
    );
};

export const RiskReturnScatter = ({ points }: RiskReturnScatterProps) => {
    if (!points.length) {
        return (
            <p className="muted text-sm">
                No view returns to plot — the ML X-ray has no active views for this date.
            </p>
        );
    }

    const longs = points.filter((p) => p.side >= 0);
    const shorts = points.filter((p) => p.side < 0);

    return (
        <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 12, right: 20, bottom: 28, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                    <XAxis
                        type="number"
                        dataKey="volatility"
                        name="Volatility"
                        unit="%"
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        tickLine={false}
                        label={{
                            value: 'Risk — Annualised Volatility (%)',
                            position: 'bottom',
                            offset: 8,
                            fontSize: 11,
                            fill: '#94a3b8',
                        }}
                    />
                    <YAxis
                        type="number"
                        dataKey="expectedReturn"
                        name="View return Q"
                        unit="%"
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        tickLine={false}
                        label={{
                            value: 'Edge — View Return Q (%)',
                            angle: -90,
                            position: 'insideLeft',
                            fontSize: 11,
                            fill: '#94a3b8',
                        }}
                    />
                    <ZAxis
                        type="number"
                        dataKey="weight"
                        range={[40, 520]}
                        name="Weight"
                        unit="%"
                    />
                    <Tooltip
                        content={<ScatterTooltip />}
                        cursor={{ strokeDasharray: '3 3' }}
                    />
                    <Scatter
                        name="Long"
                        data={longs}
                        fill="#10b981"
                        fillOpacity={0.65}
                    />
                    <Scatter
                        name="Short"
                        data={shorts}
                        fill="#f43f5e"
                        fillOpacity={0.65}
                    />
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
};

export default RiskReturnScatter;
